import { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { supabase } from './lib/supabaseClient'

function ResetPassword() {
  const navigate = useNavigate()
  const location = useLocation()
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [hasToken, setHasToken] = useState(false)

  useEffect(() => {
    const hash = window.location.hash
    const search = window.location.search
    
    console.log('=== RESET PASSWORD DEBUG ===')
    console.log('Full URL:', window.location.href)
    console.log('Hash:', hash)
    console.log('Search params:', search)
    
    let accessToken = null
    let refreshToken = null
    let type = null
    
    // Try to get token from hash first (Supabase format)
    if (hash && hash.includes('access_token')) {
      const hashParams = new URLSearchParams(hash.substring(1))
      accessToken = hashParams.get('access_token')
      refreshToken = hashParams.get('refresh_token')
      type = hashParams.get('type')
      console.log('✅ Found token in hash:', !!accessToken)
    }
    // Try query params (custom format)
    else if (search) {
      const queryParams = new URLSearchParams(search.substring(1))
      
      // Check for token in query params
      accessToken = queryParams.get('token') || queryParams.get('access_token')
      type = queryParams.get('type') || 'recovery'
      
      console.log('✅ Found token in query params:', !!accessToken)
      console.log('✅ Type from query:', type)
    }
    
    // If no token found yet, try to get from the URL path (some email providers add it differently)
    if (!accessToken && window.location.pathname.includes('reset-password')) {
      // Try to extract token from the URL path
      const pathSegments = window.location.pathname.split('/')
      const lastSegment = pathSegments[pathSegments.length - 1]
      if (lastSegment && lastSegment.length > 20) {
        accessToken = lastSegment
        console.log('✅ Found token in path:', accessToken)
      }
    }
    
    console.log('🔑 Final token found:', !!accessToken)
    console.log('🔑 Type:', type)
    
    if (accessToken) {
      setHasToken(true)
      
      supabase.auth.setSession({
        access_token: accessToken,
        refresh_token: refreshToken || ''
      }).then(({ data, error }) => {
        if (error) {
          console.error('Session error:', error)
          setError('Invalid or expired reset link. Please request a new one.')
          setHasToken(false)
        } else {
          console.log('✅ Session set successfully')
          setError('')
        }
      })
    } else {
      setHasToken(false)
      if (hash || search) {
        setError('Invalid reset link format. Please request a new password reset link from the login page.')
      } else {
        setError('No reset token found. Please request a new password reset link from the login page.')
      }
    }
  }, [location])

  const handleReset = async (e) => {
    e.preventDefault()
    
    if (!hasToken) {
      setError('Invalid reset link. Please request a new password reset.')
      return
    }
    
    if (password !== confirmPassword) {
      setError('Passwords do not match')
      return
    }
    
    if (password.length < 6) {
      setError('Password must be at least 6 characters')
      return
    }
    
    setLoading(true)
    setError('')
    setSuccess('')
    
    try {
      const { error } = await supabase.auth.updateUser({
        password: password
      })
      
      if (error) {
        console.error('Update password error:', error)
        setError(error.message || 'Failed to update password')
      } else {
        setSuccess('Password updated successfully! Redirecting to login...')
        setTimeout(() => {
          navigate('/login')
        }, 2000)
      }
    } catch (err) {
      console.error('Unexpected error:', err)
      setError('An unexpected error occurred. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <button onClick={() => navigate('/')} style={styles.backHome}>
          ← Back to Home
        </button>
        <div style={styles.icon}>🔐</div>
        <h1 style={styles.title}>Reset Password</h1>
        
        {error && <div style={styles.error}>{error}</div>}
        {success && <div style={styles.success}>{success}</div>}
        
        {hasToken ? (
          <form onSubmit={handleReset}>
            <p style={styles.text}>Enter your new password below.</p>
            <input
              type="password"
              placeholder="New password (min 6 characters)"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              style={styles.input}
              required
            />
            <input
              type="password"
              placeholder="Confirm new password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              style={styles.input}
              required
            />
            <button
              type="submit"
              disabled={loading}
              style={styles.button}
            >
              {loading ? 'Updating...' : 'Update Password'}
            </button>
          </form>
        ) : (
          <div>
            <p style={styles.text}>
              Please request a new password reset link from the login page.
            </p>
            <button
              onClick={() => navigate('/login')}
              style={styles.button}
            >
              Go to Login
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

const styles = {
  container: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #1e3a5f 0%, #0f2b42 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
    fontFamily: "'Inter', system-ui, sans-serif"
  },
  card: {
    background: '#fff',
    borderRadius: '24px',
    padding: '40px',
    width: '100%',
    maxWidth: '450px',
    boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
    position: 'relative',
    textAlign: 'center'
  },
  backHome: {
    position: 'absolute',
    top: '20px',
    left: '20px',
    background: 'none',
    border: 'none',
    color: '#64748b',
    cursor: 'pointer',
    fontSize: '14px'
  },
  icon: { fontSize: '48px', marginBottom: '16px' },
  title: { fontSize: '28px', fontWeight: '700', color: '#1e3a5f', marginBottom: '8px' },
  text: { fontSize: '16px', color: '#64748b', marginBottom: '20px' },
  input: {
    width: '100%',
    padding: '14px 16px',
    margin: '8px 0',
    border: '1px solid #e2e8f0',
    borderRadius: '12px',
    fontSize: '16px',
    fontFamily: 'inherit',
    boxSizing: 'border-box'
  },
  button: {
    width: '100%',
    padding: '14px',
    marginTop: '16px',
    background: '#1e3a5f',
    color: '#fff',
    border: 'none',
    borderRadius: '12px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer'
  },
  error: {
    background: '#fee2e2',
    color: '#dc2626',
    padding: '12px',
    borderRadius: '12px',
    marginBottom: '16px',
    fontSize: '14px'
  },
  success: {
    background: '#d1fae5',
    color: '#059669',
    padding: '12px',
    borderRadius: '12px',
    marginBottom: '16px',
    fontSize: '14px'
  }
}

export default ResetPassword